import React from 'react';
import { useParams } from 'react-router-dom';
import { normalizeFormError, Uuid } from 'app/utils';
import { StoreSelectors, useStoreSelector } from 'app/store';
import {
  useDialog,
  useInsurerGetInsurerQuery,
  useOwnerInsurerQuery,
  useEditInsurerOwnerMutation,
  useSnakeBar,
} from 'app/hooks';
import { HasAccessTo, IsOwnerAdmin } from 'app/components';
import { INSURER } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';

export const useInsurerInformationTab = () => {
  const { insurerId } = useParams();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const isOwner = IsOwnerAdmin();
  const canEdit = HasAccessTo(INSURER, EDIT);

  const ownerId = useStoreSelector<string>(state => StoreSelectors.AppSelector.selectOwnerId(state.AppReducer));

  const {
    dialogState: editModalOpen,
    openDialog: handleOpenEditModal,
    closeDialog: handleCloseEditModal,
  } = useDialog();

  const { data: insurer, isLoading: isLoadingInsurer } = useInsurerGetInsurerQuery({
    variables: { insurerId },
    options: { enabled: !!insurerId },
  });

  const { data: insurerOwner, isLoading: isLoadingOwnerInsurer } = useOwnerInsurerQuery({
    ownerId,
    insurerId,
  });

  const insurerContacts = insurerOwner?.contacts ?? [];

  const { mutateAsync: editInsurerOwner } = useEditInsurerOwnerMutation();

  const isLoading = isLoadingInsurer || isLoadingOwnerInsurer;

  const handleEditInsurer = React.useCallback(
    async (values: Record<string, any>, form: any) => {
      const method = 'editInsurer';

      try {
        const newValues = {
          ...values,
          contacts: values.contacts.map((contact: Record<string, any>) => ({
            ...contact,
            contactId: contact.contactId || Uuid.newV4(),
            ownerInsurerId: insurerOwner?.ownerInsurerId,
          })),
          ownerInsurerId: insurerOwner?.ownerInsurerId,
        };

        if (insurerId) {
          await editInsurerOwner({ ownerId, insurerId, values: newValues });
          handleCloseEditModal();
          showSuccessSnakeBar({ method });
          form.resetForm();
        }
      } catch (error: any) {
        const errors = normalizeFormError(error, form);
        if (!errors.violations) {
          handleCloseEditModal();
          showErrorSnakeBar({ method });
          throw error;
        }
      }
    },
    [
      editInsurerOwner,
      handleCloseEditModal,
      insurerId,
      ownerId,
      showErrorSnakeBar,
      showSuccessSnakeBar,
      insurerOwner?.ownerInsurerId,
    ],
  );

  return {
    insurerId,
    insurer,
    insurerOwner,
    insurerContacts,
    isLoading,
    isLoadingInsurer,
    isOwner,
    canEdit,
    editModalOpen,
    handleOpenEditModal,
    handleCloseEditModal,
    handleEditInsurer,
  };
};
